import { DateTime } from "luxon";
import { observable, makeObservable } from "mobx";
import { observer } from "mobx-react";
import React, { Component } from "react";
import MaskedInput from "react-text-mask";
import Swal from "sweetalert2";
import className from "../../shared/className";
import {
  convertFormattedDateTimeToISO,
  formatDate,
  formatTime,
  isValidTime,
  toLuxon,
} from "../../shared/dateTimeUtils";

class TimeModal extends Component {
  selectedDate;
  value = DateTime.now();

  constructor(props) {
    super(props);

    makeObservable(this, {
      selectedDate: observable,
      value: observable,
    });

    this.value = toLuxon(props.value);

    this.state = {
      timeText: formatTime(toLuxon(this.value).toISO()),
      dateText: formatDate(toLuxon(this.value).toISO()),
      valid: true,
    };

    this.dateOptions = getDateOptions(props.hours, props.now);

    this.props.onChange(this.value);

    this.dateKeyDown = this.dateKeyDown.bind(this);
    this.checkValid = this.checkValid.bind(this);
  }

  componentDidMount() {
    // The text-mask core library blocks key events, this is the only way to get them.
    this.refDate?.inputElement.addEventListener(
      "keyup",
      this.dateKeyDown,
      false,
    );
  }

  componentWillUnmount() {
    this.refDate?.inputElement.removeEventListener("keyup", this.dateKeyDown);
    clearTimeout(this.datesTimeout);
  }

  render() {
    return (
      <div
        data-testid="date-time-picker"
        className="date-time-picker"
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            this.fillDateSeconds();
            if (this.checkValid()) {
              Swal.close();
            }
          }
        }}
      >
        <form
          ref={(r) => (this.refForm = r)}
          onSubmit={(e) => e.preventDefault()}
        >
          {this.props.hours > 24 ? (
            <label>
              <MaskedInput
                ref={(r) => (this.refDate = r)}
                value={this.state.dateText}
                mask={[
                  /\d/,
                  /\d/,
                  "/",
                  /\d/,
                  /\d/,
                  "/",
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                ]}
                onChange={this.dateChange}
                pattern="[0-3]\d/[0-3]\d/\d\d\d\d"
                onFocus={(e) => {
                  e.target.select();
                  this.setState({ showDates: true });
                }}
                onBlur={() => {
                  this.checkValid();
                  clearTimeout(this.datesTimeout);
                  this.datesTimeout = setTimeout(() => {
                    this.setState({ showDates: false });
                  }, 300);
                }}
                list="dateOptions"
                autoFocus={true}
                id="date"
              />
              {this.state.showDates ? (
                <div className="date-options">
                  {this.dateOptions.map((dateOption) => (
                    <div
                      {...className({
                        option: 1,
                        selected: dateOption === this.selectedDateOption,
                      })}
                      key={dateOption}
                      onClick={() => {
                        this.setState({ dateText: dateOption });
                        this.bracketTime({ dateText: dateOption });
                      }}
                    >
                      {dateOption}
                    </div>
                  ))}
                </div>
              ) : null}
            </label>
          ) : null}
          <MaskedInput
            defaultValue={this.state.timeText}
            mask={[/[0-2]/, /\d/, ":", /[0-5]/, /\d/, ":", /[0-5]/, /\d/]}
            onChange={this.timeChange}
            pattern="[0-2][0-9]:[0-5][0-9]:([0-5][0-9]|__)"
            onFocus={(e) => e.target.select()}
            onBlur={() => {
              this.fillDateSeconds();
              this.checkValid();
            }}
            ref={(r) => (this.refTime = r)}
            id="time"
          />
        </form>
        {this.state.valid ? null : <p>Time Selected Invalid</p>}
      </div>
    );
  }

  fillDateSeconds() {
    const $time = this.refTime.inputElement;
    $time.value = this.fillSeconds($time.value);
  }

  fillSeconds(time) {
    return time.match(/[0-2][0-9]:[0-5][0-9]:__/)
      ? time.replace(/_/g, 0)
      : time;
  }

  checkValid() {
    const valid = this.refForm.checkValidity();
    this.setState({
      valid,
    });
    return valid;
  }

  timeChange = (e) => {
    if (!e.target.validity.valid) {
      this.valid = false;
      return;
    }
    this.setState({
      timeText: e.target.value,
    });
    this.bracketTime({
      timeText: e.target.value,
    });
  };

  get selectedDateOption() {
    return this.dateOptions.find(
      (dateOption) => dateOption === this.state.dateText,
    );
  }

  dateKeyDown(e) {
    if (this.selectedDateOption) {
      const index = this.dateOptions.indexOf(this.selectedDateOption);
      if (
        (e.key === "ArrowDown" || e.key === "Down") &&
        this.dateOptions[index + 1]
      ) {
        this.setState({
          dateText: this.dateOptions[index + 1],
        });
        this.bracketTime({
          dateText: this.dateOptions[index + 1],
        });
      } else if (
        (e.key === "ArrowUp" || e.key === "Up") &&
        this.dateOptions[index - 1]
      ) {
        this.setState({
          dateText: this.dateOptions[index - 1],
        });
        this.bracketTime({
          dateText: this.dateOptions[index - 1],
        });
      }
    }
  }
  dateChange = (e) => {
    if (!e.target.validity.valid) {
      this.valid = true;
      return;
    }
    if (!this.dateOptions.includes(e.target.value)) {
      e.target.value = this.dateOptions[0];
      this.valid = true;
    }
    this.setState({
      dateText: e.target.value,
    });
    this.bracketTime({
      dateText: e.target.value,
    });
  };

  bracketTime(update = {}) {
    const { dateText, timeText } = { ...this.state, ...update };

    let value = toLuxon(
      convertFormattedDateTimeToISO(dateText, this.fillSeconds(timeText)),
    );

    if (!value.isValid) {
      this.valid = false;
    }

    const now = DateTime.now().minus({ milliseconds: this.props.latency || 0 });

    // Set date as yesterday if time is in the future (24-hour configurations).
    if (this.props.hours <= 24) {
      if (value > now) {
        value = value.minus({ days: 1 });
      } else if (value.weekday < now.weekday) {
        let yesterday = now.minus({ days: 1 });
        if (value < yesterday) {
          value = value.plus({ days: 1 });
        }
      }
    } else {
      // Bracket time between configured hours ago and now.
      value = DateTime.min(
        toLuxon(now),
        DateTime.max(toLuxon(now).minus({ hours: this.props.hours }), value),
      );
    }

    this.value = toLuxon(value);

    if (isValidTime(this.value)) {
      this.props.onChange(this.value);
      this.valid = true;
      this.setState({ valid: true });
    }
  }
}

export default observer(TimeModal);

export const getDateOptions = (hours, now) => {
  const DAY_HOURS = 24;
  const DAYS = hours / DAY_HOURS + 1;
  let options = [];
  for (let i = 0; i < DAYS; i++) {
    options.push(formatDate(toLuxon(now).minus({ days: i })));
  }

  return options;
};
